import { ENDPOINTS } from '../utils/endpoints'
import { getAPIwithToken, postAPIwithToken } from '../utils/fetchApi'

export interface ProductCategory {
  id: string | number
  name: string
  slug?: string
  is_selected?: boolean
}

export interface ProductActions {
  can_view_warehouse: boolean
  can_assign_warehouse: boolean
}

export interface ProductCrate {
  id?: number
  name?: string
  units?: number
}

export interface ProductItem {
  id: number
  category_id: string | number
  category_name: string
  name: string
  sku_code: string
  mrp: number
  gst_percent: number
  price: number
  units_per_case: number
  is_active: number
  variant: string
  brand: string
  sub_brand: string | null
  size: string
  crate?: ProductCrate | null
  crate_name?: string
  business_units: string | null
  created_by: string
  actions?: ProductActions
}

export interface ProductSummary {
  total_count: number
  active_count: number
  inactive_count: number
}

export interface ProductPagination {
  total_records: number
  page: number
  limit: number
  total_pages: number
  showing_from: number
  showing_to: number
}

export interface GetProductsData {
  summary: ProductSummary
  categories: ProductCategory[]
  products: ProductItem[]
  pagination: ProductPagination
}

export interface GetProductsResponse {
  success: boolean
  statusCode: number
  message: string
  data: GetProductsData
}

export interface FetchProductsParams {
  category_id?: string
  page?: number
  limit?: number
  search?: string
  status?: string
}

export async function getProducts(params?: FetchProductsParams): Promise<GetProductsResponse> {
  let endpoint = ENDPOINTS.GET_PRODUCTS

  if (params) {
    const query = new URLSearchParams()
    if (params.category_id && params.category_id !== 'cat_all' && params.category_id !== 'all') {
      query.append('category_id', params.category_id)
    }
    if (params.page) {
      query.append('page', params.page.toString())
    }
    if (params.limit) {
      query.append('limit', params.limit.toString())
    }
    if (params.search) {
      query.append('search', params.search)
    }
    if (params.status && params.status !== 'All') {
      query.append('status', params.status.toLowerCase())
    }
    const queryString = query.toString()
    if (queryString) {
      endpoint += `?${queryString}`
    }
  }

  return getAPIwithToken<GetProductsResponse>(endpoint)
}

export async function getProductById(id: string | number): Promise<any> {
  const endpoint = typeof ENDPOINTS.GET_PRODUCT_BY_ID === 'function'
    ? ENDPOINTS.GET_PRODUCT_BY_ID(id)
    : `api/v1/erp/products/${id}`
  return getAPIwithToken<any>(endpoint)
}

export async function getProductDropdowns(): Promise<any> {
  return getAPIwithToken(ENDPOINTS.GET_PRODUCT_DROPDOWNS)
}

export async function createProduct(payload: any): Promise<any> {
  const formData = new FormData()
  formData.append('source', 'Desktop')

  // Text fields matching backend cURL --form payload
  const fields = [
    'name',
    'displayName',
    'mrp',
    'price',
    'gstPercentage',
    'hsnCode',
    'category_id',
    'brand_id',
    'size_id',
    'unit_id',
    'crate_id',
    'crate_qty',
    'stockQty',
    'description',
  ]

  fields.forEach((field) => {
    const val = payload[field]
    if (val !== undefined && val !== null) {
      formData.append(field, String(val))
    }
  })

  // Handle single main-img File upload
  const mainImg = payload['main-img']
  if (mainImg instanceof File) {
    formData.append('main-img', mainImg)
  } else if (typeof mainImg === 'string' && mainImg.startsWith('blob:')) {
    try {
      const blobRes = await fetch(mainImg)
      const blob = await blobRes.blob()
      formData.append('main-img', blob, 'main-img.png')
    } catch (e) {
      console.warn('Could not convert blob url to file:', e)
    }
  }

  // Handle multiple slider-img Files upload
  const sliderImgs = payload['slider-img']
  if (Array.isArray(sliderImgs)) {
    for (const item of sliderImgs) {
      if (item instanceof File) {
        formData.append('slider-img', item)
      } else if (typeof item === 'string' && item.startsWith('blob:')) {
        try {
          const blobRes = await fetch(item)
          const blob = await blobRes.blob()
          formData.append('slider-img', blob, 'slider-img.png')
        } catch (e) {
          console.warn('Could not convert blob url to file:', e)
        }
      }
    }
  }

  return postAPIwithToken(ENDPOINTS.CREATE_PRODUCT, formData)
}

export interface ProductPriceItem {
  id?: number;
  productId: number;
  zoneId?: number;
  cityId?: number;
  name?: string;
  displayName?: string;
  sapCode?: string;
  sizeValue?: number | string;
  unit?: string;
  mrp?: number;
  price?: number;
  cityName?: string;
  zoneName?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  distributorMargin?: number;
  retailerMargin?: number;
  tradeMargin?: number;
  distributorPrice?: number;
  retailerPrice?: number;
  product?: {
    id?: number;
    name?: string;
    displayName?: string;
    sapCode?: string;
  };
  DistributorMargin?: any;
  RetailerMargin?: any;
  TradeMargin?: any;
  createdAt?: string;
  updatedAt?: string;
  status?: number;
}

export async function getProductPricesAPI(cityId: number | string): Promise<any> {
  try {
    return await getAPIwithToken(`${ENDPOINTS.GET_PRODUCT_PRICES}?cityId=${cityId}`);
  } catch {
    return await getAPIwithToken(`erp/products/prices?cityId=${cityId}`);
  }
}

export interface FetchMarginParams {
  productId?: number | string | null;
  cityId?: number | string | null;
  zoneId?: number | string | null;
}

export interface MarginItem {
  id: number;
  product?: {
    id?: number;
    name?: string;
    displayName?: string;
    sapCode?: string;
  };
  margin_amount?: number;
  discount?: number;
  marginAmount?: number;
  status?: number;
  createdBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface MarginDataResponse {
  status: string;
  message?: string;
  data?: MarginItem[] | MarginItem | any;
}

export async function getDistributorMarginAPI(params?: FetchMarginParams): Promise<MarginDataResponse> {
  const query = new URLSearchParams();
  if (params?.productId) query.append('productId', String(params.productId));
  if (params?.cityId) query.append('cityId', String(params.cityId));
  if (params?.zoneId) query.append('zoneId', String(params.zoneId));
  const queryString = query.toString();
  return getAPIwithToken<MarginDataResponse>(
    queryString ? `${ENDPOINTS.GET_DISTRIBUTOR_MARGINS}?${queryString}` : ENDPOINTS.GET_DISTRIBUTOR_MARGINS
  );
}

export async function getRetailerMarginAPI(params?: FetchMarginParams): Promise<MarginDataResponse> {
  const query = new URLSearchParams();
  if (params?.productId) query.append('productId', String(params.productId));
  if (params?.cityId) query.append('cityId', String(params.cityId));
  if (params?.zoneId) query.append('zoneId', String(params.zoneId));
  const queryString = query.toString();
  return getAPIwithToken<MarginDataResponse>(
    queryString ? `${ENDPOINTS.GET_RETAILER_MARGINS}?${queryString}` : ENDPOINTS.GET_RETAILER_MARGINS
  );
}

export async function getTradeMarginAPI(params?: FetchMarginParams): Promise<MarginDataResponse> {
  const query = new URLSearchParams();
  if (params?.productId) query.append('productId', String(params.productId));
  if (params?.cityId) query.append('cityId', String(params.cityId));
  if (params?.zoneId) query.append('zoneId', String(params.zoneId));
  const queryString = query.toString();
  return getAPIwithToken<MarginDataResponse>(
    queryString ? `${ENDPOINTS.GET_TRADE_MARGINS}?${queryString}` : ENDPOINTS.GET_TRADE_MARGINS
  );
}


